import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { useState } from 'react'
import { Button } from '../components/ui/button.js'
import { Dialog } from '../components/ui/dialog.js'
import { CheckboxField, TextField } from '../components/ui/field.js'
import { Alert, Badge } from '../components/ui/feedback.js'
import { format, t } from '../i18n/index.js'
import { cn } from '../lib/cn.js'
import { createEvent, deleteEvent, listEvents, updateEvent, type EventView } from '../lib/events.js'

/**
 * Split out so the id is narrowed by the caller rather than asserted here.
 * The button only exists while editing an existing fair, and saying that with
 * a type is better than saying it with a `!`.
 */
function DeleteButton({
  id,
  disabled,
  onDelete,
}: {
  id: string
  disabled: boolean
  onDelete: (id: string) => void
}) {
  const strings = t().events

  return (
    <Button
      variant="danger"
      className="mr-auto"
      disabled={disabled}
      onClick={() => {
        if (window.confirm(strings.deleteConfirm)) onDelete(id)
      }}
    >
      {strings.delete}
    </Button>
  )
}

/**
 * The pinned field catalogue, and the one way to move it.
 *
 * Pinning is the whole point: a version published mid-fair must not change the
 * form under somebody standing at a booth. Lifting is therefore a deliberate
 * act with a confirmation that says plainly what it does to the devices.
 */
function SchemaVersionField({
  id,
  pinned,
  latest,
  busy,
  onLift,
}: {
  id: string
  pinned: number | null
  latest: number | null
  busy: boolean
  onLift: (id: string, version: number) => void
}) {
  const strings = t().events
  const behind = pinned !== null && latest !== null && latest > pinned

  return (
    <div className="border-border bg-surface-sunken rounded-lg border p-3">
      <p className="text-sm font-medium">
        {pinned === null
          ? strings.schemaVersionNone
          : format(strings.schemaVersion, { version: pinned })}
      </p>
      <p className="text-text-muted mt-1 text-xs">{strings.schemaVersionHint}</p>

      {behind ? (
        <Button
          size="sm"
          variant="secondary"
          className="mt-3"
          disabled={busy}
          onClick={() => {
            if (window.confirm(format(strings.liftConfirm, { latest }))) onLift(id, latest)
          }}
        >
          {format(strings.lift, { latest })}
        </Button>
      ) : (
        <p className="text-text-muted mt-2 text-xs">{strings.schemaVersionCurrent}</p>
      )}
    </div>
  )
}

interface Draft {
  id: string | null
  code: string
  name: string
  location: string
  startsOn: string
  endsOn: string
  isActive: boolean
  codeEditable: boolean
  schemaVersion: number | null
  latestSchemaVersion: number | null
}

function emptyDraft(): Draft {
  return {
    id: null,
    code: '',
    name: '',
    location: '',
    startsOn: '',
    endsOn: '',
    isActive: true,
    codeEditable: true,
    schemaVersion: null,
    latestSchemaVersion: null,
  }
}

function draftFrom(event: EventView): Draft {
  return {
    id: event.id,
    code: event.code,
    name: event.name,
    location: event.location ?? '',
    startsOn: event.startsOn ?? '',
    endsOn: event.endsOn ?? '',
    isActive: event.isActive,
    codeEditable: event.codeEditable,
    schemaVersion: event.schemaVersion,
    latestSchemaVersion: event.latestSchemaVersion,
  }
}

/**
 * The fairs.
 *
 * The code carries more weight than a name does: every article's running number
 * is built from it, so `AMB27` produces `AMB27-0001`, and those numbers end up
 * written on physical samples. The form says so, and stops offering the field
 * once the first article exists.
 */
export function EventsPage() {
  const strings = t().events
  const queryClient = useQueryClient()

  const [showInactive, setShowInactive] = useState(false)
  const [draft, setDraft] = useState<Draft | null>(null)
  const [problem, setProblem] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  const query = useQuery({
    queryKey: ['events', showInactive],
    queryFn: () => listEvents(showInactive),
  })

  async function refresh() {
    await queryClient.invalidateQueries({ queryKey: ['events'] })
  }

  const save = useMutation({
    mutationFn: async (input: Draft) => {
      const shared = {
        name: input.name,
        location: input.location === '' ? null : input.location,
        startsOn: input.startsOn === '' ? null : input.startsOn,
        endsOn: input.endsOn === '' ? null : input.endsOn,
      }

      if (input.id === null) return createEvent({ code: input.code, ...shared })

      return updateEvent(input.id, {
        // Only sent while it is still allowed. Sending an unchanged code would
        // be harmless, but sending a changed one after the first article is
        // refused — and the form should not offer what the server will reject.
        ...(input.codeEditable ? { code: input.code } : {}),
        ...shared,
        isActive: input.isActive,
      })
    },
    onSuccess: async (_result, input) => {
      await refresh()
      setDraft(null)
      setProblem(null)
      setNotice(input.id === null ? strings.created : strings.saved)
    },
    onError: (error: Error) => setProblem(error.message),
  })

  const lift = useMutation({
    mutationFn: ({ id, version }: { id: string; version: number }) =>
      updateEvent(id, { schemaVersion: version }),
    onSuccess: async () => {
      await refresh()
      setDraft(null)
      setProblem(null)
      setNotice(strings.lifted)
    },
    onError: (error: Error) => setProblem(error.message),
  })

  const remove = useMutation({
    mutationFn: (id: string) => deleteEvent(id),
    onSuccess: async () => {
      await refresh()
      setDraft(null)
      setProblem(null)
      setNotice(strings.deletedNotice)
    },
    onError: (error: Error) => setProblem(error.message),
  })

  const events = query.data ?? []

  const saveable =
    draft !== null &&
    draft.name.trim() !== '' &&
    (!draft.codeEditable || /^[A-Z][A-Z0-9]*$/.test(draft.code))

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">{strings.title}</h1>
          <p className="text-text-muted text-sm">{strings.subtitle}</p>
        </div>
        <Button onClick={() => setDraft(emptyDraft())}>{strings.create}</Button>
      </header>

      {notice !== null && <Alert tone="success">{notice}</Alert>}
      {problem !== null && <Alert tone="danger">{problem}</Alert>}
      {query.isError && <Alert tone="danger">{strings.loadError}</Alert>}

      <div className="flex items-center justify-between">
        <p className="text-text-muted text-sm">
          {query.isPending
            ? t().app.loading
            : events.length === 1
              ? strings.countOne
              : format(strings.countMany, { count: events.length })}
        </p>
        <CheckboxField
          label={strings.showInactive}
          checked={showInactive}
          onChange={(event) => setShowInactive(event.target.checked)}
        />
      </div>

      {!query.isPending && events.length === 0 ? (
        <p className="border-border rounded-lg border border-dashed p-8 text-center text-sm text-slate-500">
          {strings.empty}
        </p>
      ) : (
        <ul className="grid gap-3 sm:grid-cols-2" data-testid="event-list">
          {events.map((event) => (
            <li
              key={event.id}
              className={cn(
                'border-border bg-surface-raised flex flex-col gap-3 rounded-lg border p-4',
                !event.isActive && 'opacity-60',
              )}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="flex flex-wrap items-center gap-2 font-medium">
                    {event.name}
                    {!event.isActive && <Badge tone="warning">{strings.inactive}</Badge>}
                  </p>
                  <p className="text-text-muted text-xs">
                    <code>{event.code}</code>
                    {event.location !== null && ` · ${event.location}`}
                  </p>
                </div>
                <Button size="sm" variant="secondary" onClick={() => setDraft(draftFrom(event))}>
                  {strings.edit}
                </Button>
              </div>

              <p className="text-text-muted text-sm">
                {event.startsOn !== null && event.endsOn !== null
                  ? `${new Date(event.startsOn).toLocaleDateString('de-DE')} – ${new Date(event.endsOn).toLocaleDateString('de-DE')}`
                  : strings.noDates}
              </p>

              <p className="text-text-muted text-xs">
                {event.schemaVersion === null
                  ? strings.schemaVersionNone
                  : event.latestSchemaVersion !== null &&
                      event.latestSchemaVersion > event.schemaVersion
                    ? format(strings.schemaVersionBehind, {
                        version: event.schemaVersion,
                        latest: event.latestSchemaVersion,
                      })
                    : format(strings.schemaVersion, { version: event.schemaVersion })}
              </p>

              {event.itemCount > 0 ? (
                <Link
                  to="/artikel"
                  search={{ eventId: event.id }}
                  className="text-accent text-sm hover:underline"
                >
                  {format(strings.itemCount, { count: event.itemCount })}
                </Link>
              ) : (
                <span className="text-text-muted text-sm">{strings.noItems}</span>
              )}
            </li>
          ))}
        </ul>
      )}

      <Dialog
        open={draft !== null}
        onClose={() => setDraft(null)}
        title={draft?.id === null ? strings.create : strings.edit}
        footer={
          <>
            {draft?.id != null && (
              <DeleteButton
                id={draft.id}
                disabled={remove.isPending}
                onDelete={(id) => remove.mutate(id)}
              />
            )}
            <Button variant="secondary" onClick={() => setDraft(null)}>
              {strings.cancel}
            </Button>
            <Button
              disabled={!saveable || save.isPending}
              onClick={() => {
                if (draft) save.mutate(draft)
              }}
            >
              {save.isPending ? strings.saving : strings.save}
            </Button>
          </>
        }
      >
        {draft && (
          <div className="flex flex-col gap-4">
            <TextField
              label={strings.code}
              hint={draft.codeEditable ? strings.codeHint : strings.codeFrozen}
              value={draft.code}
              disabled={!draft.codeEditable}
              onChange={(event) => setDraft({ ...draft, code: event.target.value.toUpperCase() })}
            />

            <TextField
              label={strings.name}
              value={draft.name}
              onChange={(event) => setDraft({ ...draft, name: event.target.value })}
            />

            <TextField
              label={strings.location}
              value={draft.location}
              onChange={(event) => setDraft({ ...draft, location: event.target.value })}
            />

            <div className="grid gap-4 sm:grid-cols-2">
              <TextField
                label={strings.startsOn}
                type="date"
                value={draft.startsOn}
                onChange={(event) => setDraft({ ...draft, startsOn: event.target.value })}
              />
              <TextField
                label={strings.endsOn}
                type="date"
                value={draft.endsOn}
                onChange={(event) => setDraft({ ...draft, endsOn: event.target.value })}
              />
            </div>

            {draft.id !== null && (
              <SchemaVersionField
                id={draft.id}
                pinned={draft.schemaVersion}
                latest={draft.latestSchemaVersion}
                busy={lift.isPending}
                onLift={(id, version) => lift.mutate({ id, version })}
              />
            )}

            {draft.id !== null && (
              <CheckboxField
                label={strings.active}
                hint={strings.activeHint}
                checked={draft.isActive}
                onChange={(event) => setDraft({ ...draft, isActive: event.target.checked })}
              />
            )}
          </div>
        )}
      </Dialog>
    </div>
  )
}
